import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Statistic,
  Tag,
  Typography,
  message
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageToolbar from "../../../../design-system/PageToolbar";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { formatCents, formatDate } from "../shared/money";
import { errorText } from "../api";
import * as api from "./api";

const toIso = d => (d ? dayjs(d).format("YYYY-MM-DD") : "");
const IN_CATEGORIES = ["revenue", "private_deposit", "bank", "other_income"];

const EntryModal = ({ open, categories, projects, onClose, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const direction = Form.useWatch("direction", form);
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ date: dayjs(), direction: "out", taxRate: 19, category: "materials" });
    }
  }, [open, form]);
  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await api.addCashEntry({ ...v, date: toIso(v.date) });
      message.success(t("Entry booked"));
      onSaved();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };
  const options =
    direction === "in"
      ? IN_CATEGORIES.map(k => ({ value: k, label: t(`cash_category_${k}`) }))
      : [
          ...categories.map(c => ({ value: c.key, label: c.label })),
          { value: "bank", label: t("cash_category_bank") }
        ];
  return (
    <Modal
      open={open}
      title={t("Add cash entry")}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Book")}
      cancelText={t("Cancel")}
      rootClassName="acct-module"
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="direction" label={t("Direction")}>
              <Radio.Group
                onChange={e =>
                  form.setFieldsValue({
                    category: e.target.value === "in" ? "revenue" : "materials"
                  })
                }
                options={[
                  { value: "in", label: t("Receipt") },
                  { value: "out", label: t("Payment") }
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="date" label={t("Date")} rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="amount" label={t("Amount (gross)")} rules={[{ required: true }]}>
              <MoneyInput />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="taxRate" label={t("VAT rate")}>
              <Select options={[19, 7, 0].map(r => ({ value: r, label: `${r} %` }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="category" label={t("Category")} rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={options} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="costCentre" label={t("Project")}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={projects.map(p => ({ value: p.id, label: p.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="text" label={t("Text")} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="beleg" label={t("Beleg ID (optional)")}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CloseDayModal = ({ open, date, booked, onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [counted, setCounted] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setCounted(booked);
  }, [open, booked]);
  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.closeCashDay(date, counted);
      const r = res.data;
      message.success(
        r.difference
          ? `${t("Day closed, difference booked")}: ${formatCents(r.difference)}`
          : t("Day closed without difference")
      );
      onDone();
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={open}
      title={`${t("Close day (Kassensturz)")} ${formatDate(date)}`}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Close day")}
      cancelText={t("Cancel")}
      rootClassName="acct-module"
    >
      <p>
        {t("Booked balance")}: <Amount cents={booked} strong />
      </p>
      <Form layout="vertical">
        <Form.Item label={t("Counted cash")}>
          <MoneyInput value={counted} onChange={setCounted} />
        </Form.Item>
      </Form>
      {counted !== null && counted !== booked ? (
        <Typography.Text type="warning">
          {t("Difference")}: <span className="acct-num">{formatCents(counted - booked)}</span> —{" "}
          {t("will be booked as cash difference")}
        </Typography.Text>
      ) : null}
    </Modal>
  );
};

const ImportModal = ({ open, onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) form.setFieldsValue({ date: dayjs(), g19: 0, g7: 0, g0: 0 });
  }, [open, form]);
  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await api.importDailyTotals({
        date: toIso(v.date),
        grossByRate: { 19: v.g19 || 0, 7: v.g7 || 0, 0: v.g0 || 0 },
        text: v.text || ""
      });
      message.success(t("Daily totals imported"));
      onDone();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={open}
      title={t("Import daily totals (Z-Bon)")}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Import")}
      cancelText={t("Cancel")}
      rootClassName="acct-module"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="date" label={t("Date")} rules={[{ required: true }]}>
          <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="g19" label={`${t("Gross revenue")} 19 %`}>
          <MoneyInput />
        </Form.Item>
        <Form.Item name="g7" label={`${t("Gross revenue")} 7 %`}>
          <MoneyInput />
        </Form.Item>
        <Form.Item name="g0" label={`${t("Gross revenue")} 0 %`}>
          <MoneyInput />
        </Form.Item>
        <Form.Item name="text" label={t("Text")}>
          <Input placeholder="Z-Bon Nr." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CashbookTab = () => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [period, setPeriod] = useState(dayjs());
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entryModal, setEntryModal] = useState(false);
  const [closeDay, setCloseDay] = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = period.format("YYYY-MM");
      const [list, rep] = await Promise.all([
        api.listCash({ period: p }),
        api.cashReport({ period: p })
      ]);
      setData(list.data);
      setReport(rep.data);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api
      .listCategories()
      .then(res => setCategories(res.data.categories || []))
      .catch(() => setCategories([]));
    api
      .listProjects()
      .then(res => setProjects(res.data.projects || []))
      .catch(() => setProjects([]));
  }, []);

  const lastDate = data?.entries?.length
    ? data.entries[data.entries.length - 1].date
    : toIso(dayjs());
  const lastOpen = data?.entries
    ?.filter(e => !e.dayClosedAt)
    .map(e => e.date)
    .pop();

  return (
    <div>
      <PageToolbar
        filters={<DatePicker picker="month" value={period} onChange={v => v && setPeriod(v)} />}
        actions={
          <>
            <Button type="primary" onClick={() => setEntryModal(true)}>
              {t("Add cash entry")}
            </Button>
            <Button disabled={!lastOpen} onClick={() => setCloseDay(lastOpen || lastDate)}>
              {t("Close day (Kassensturz)")}
            </Button>
            <Button onClick={() => setImportModal(true)}>{t("Import daily totals (Z-Bon)")}</Button>
            <Button onClick={() => setShowReport(s => !s)} aria-pressed={showReport}>
              {t("Cash report")}
            </Button>
          </>
        }
      />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          [t("Opening balance"), data?.opening],
          [t("Receipts"), report?.totalIn],
          [t("Payments"), report?.totalOut],
          [t("Cash balance"), data?.closing]
        ].map(([title, cents]) => (
          <Col xs={12} md={6} key={title}>
            <Card size="small" className="acct-kpi">
              <Statistic title={title} value={formatCents(cents)} />
            </Card>
          </Col>
        ))}
      </Row>
      {showReport && report ? (
        <Card size="small" title={t("Cash report")} style={{ marginBottom: 12 }}>
          <ResponsiveTable
            size="small"
            pagination={false}
            rowKey="date"
            dataSource={report.days}
            columns={[
              { title: t("Date"), dataIndex: "date", render: formatDate, mobile: "title" },
              {
                title: t("Closing balance"),
                dataIndex: "closing",
                align: "end",
                mobile: "subtitle",
                render: v => <Amount cents={v} strong={isMobile} />
              },
              { title: t("Entries"), dataIndex: "count", align: "end" },
              {
                title: t("Receipts"),
                dataIndex: "in",
                align: "end",
                render: v => <Amount cents={v} />
              },
              {
                title: t("Payments"),
                dataIndex: "out",
                align: "end",
                render: v => <Amount cents={v} />
              },
              {
                title: t("Day closed"),
                dataIndex: "closed",
                render: c => (c ? <Tag color="green">{t("closed")}</Tag> : <Tag>{t("open")}</Tag>)
              }
            ]}
          />
          <Typography.Text type="secondary" className="acct-wrap">
            {t("Revenue by VAT rate")}:{" "}
            {Object.entries(report.revenueByRate || {})
              .map(([r, v]) => `${r} %: ${formatCents(v)}`)
              .join(" · ") || "-"}
          </Typography.Text>
        </Card>
      ) : null}
      <ResponsiveTable
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={data?.entries || []}
        pagination={false}
        columns={[
          { title: t("No."), dataIndex: "seq", width: 70 },
          { title: t("Date"), dataIndex: "date", render: formatDate, width: 110 },
          { title: t("Text"), dataIndex: "text", mobile: "title" },
          {
            // Phones: one signed amount (receipt +, payment −) under the text.
            title: t("Amount"),
            key: "signedAmount",
            hidden: !isMobile,
            mobile: "subtitle",
            render: (_, e) => (
              <Amount cents={e.amountIn ? e.amountIn : -(e.amountOut || 0)} signed large />
            )
          },
          { title: t("Contra account"), dataIndex: "contraAccount", width: 100 },
          { title: t("VAT"), dataIndex: "taxRate", render: r => (r ? `${r} %` : "-"), width: 70 },
          {
            title: t("Receipt"),
            dataIndex: "amountIn",
            align: "end",
            mobile: "hidden",
            render: v => (v ? <Amount cents={v} /> : ""),
            width: 120
          },
          {
            title: t("Payment"),
            dataIndex: "amountOut",
            align: "end",
            mobile: "hidden",
            render: v => (v ? <Amount cents={v} /> : ""),
            width: 120
          },
          {
            title: t("Balance"),
            dataIndex: "balanceAfter",
            align: "end",
            render: v => <Amount cents={v} strong={isMobile} />,
            width: 130
          },
          {
            title: "",
            mobileLabel: t("Day closed"),
            dataIndex: "dayClosedAt",
            render: v =>
              isMobile ? (
                v ? (
                  <Tag icon={<LockOutlined />}>{t("closed")}</Tag>
                ) : (
                  <Tag>{t("open")}</Tag>
                )
              ) : v ? (
                <LockOutlined title={t("Day closed")} aria-label={t("Day closed")} />
              ) : null,
            width: 40
          }
        ]}
      />
      <EntryModal
        open={entryModal}
        categories={categories}
        projects={projects}
        onClose={() => setEntryModal(false)}
        onSaved={() => {
          setEntryModal(false);
          load();
        }}
      />
      <CloseDayModal
        open={Boolean(closeDay)}
        date={closeDay}
        booked={data?.closing || 0}
        onClose={() => setCloseDay(null)}
        onDone={() => {
          setCloseDay(null);
          load();
        }}
      />
      <ImportModal
        open={importModal}
        onClose={() => setImportModal(false)}
        onDone={() => {
          setImportModal(false);
          load();
        }}
      />
    </div>
  );
};

export default CashbookTab;
